import { jsx as _jsx } from "react/jsx-runtime";
// Rendered by tests with tsx, which would fall back to the classic JSX runtime: pin the automatic one (as tsc does).
/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
/**
 * The outfit gallery's grid draws only the rows on screen (owner, 2026-10-15: "when trying to scroll through outfits in
 * the selector, it's very laggy for my account with all of them", 692 outfits). Every tile is one fixed height and the
 * columns are worked out as the stylesheet's `auto-fill` does, so where each row sits is arithmetic: the grid keeps its
 * full height with a spacer row above and below the rows it draws, the scrollbar is right, and nothing reflows as rows
 * come and go. The numbers here are the stylesheet's (gyro.css `.gyro-mascot-gallery`, `.gyro-mascot-tile`, the rated
 * gallery's cells); mascotLookSettings.test.ts holds them together.
 */
export const MASCOT_GRID = Object.freeze({
    /** A tile's least width: the gallery's `minmax(78px, 1fr)` columns, `minmax(144px, 1fr)` with star ratings. */
    tileMinPx: 78,
    ratedTileMinPx: 144,
    /** The gap between cells. */
    gapPx: 8,
    /** The gallery's padding plus border on each side: the first row starts this far from the grid's edge. */
    edgePx: 6,
    /** A cell's height: the tile's fixed height; with star ratings the 24 px row of stars and its 4 px gap too. */
    rowPx: 112,
    ratedRowPx: 140,
});
/** Rows drawn beyond the ones on screen, above and below, so a scroll shows tiles at once. */
export const MASCOT_GRID_OVERSCAN_ROWS = 2;
/** Without a layout yet (a page not shown), the tiles drawn from the top: a first screen's worth on any width. */
export const MASCOT_GRID_FALLBACK_TILES = 60;
/** The columns a gallery of `widthPx` holds, as `repeat(auto-fill, minmax(<tile>, 1fr))` with the gap decides it; 0 without a width. */
export function mascotGridColumns(widthPx, rated) {
    if (!(widthPx > 0))
        return 0;
    const tile = rated ? MASCOT_GRID.ratedTileMinPx : MASCOT_GRID.tileMinPx;
    return Math.max(1, Math.floor((widthPx - 2 * MASCOT_GRID.edgePx + MASCOT_GRID.gapPx) / (tile + MASCOT_GRID.gapPx)));
}
/** The height of one row and the gap under it. */
export function mascotGridRowPitch(rated) {
    return (rated ? MASCOT_GRID.ratedRowPx : MASCOT_GRID.rowPx) + MASCOT_GRID.gapPx;
}
/** The rows of a grid to draw for what is on screen, with the overscan, clamped to the grid. */
export function mascotGridRows({ count, columns, rated, viewTop, viewBottom, overscan = MASCOT_GRID_OVERSCAN_ROWS }) {
    const rows = columns > 0 ? Math.ceil(count / columns) : 0;
    if (rows === 0)
        return { rows: 0, fromRow: 0, toRow: 0 };
    const pitch = mascotGridRowPitch(rated);
    const first = Math.floor((viewTop - MASCOT_GRID.edgePx) / pitch) - overscan;
    const last = Math.ceil((viewBottom - MASCOT_GRID.edgePx) / pitch) + overscan;
    const fromRow = Math.min(rows, Math.max(0, first));
    const toRow = Math.max(fromRow, Math.min(rows, last));
    return { rows, fromRow, toRow };
}
/** The element with focus, through a shadow root when the gallery is in one. */
export function activeElementAround(element) {
    const root = element.getRootNode();
    return root instanceof ShadowRoot ? root.activeElement : element.ownerDocument.activeElement;
}
/**
 * Where a grid scrolls: the grid itself when it is the scrolling box (the chat picker's flat gallery), else the nearest
 * ancestor that scrolls (the Settings page; the chat picker's groups), through a shadow root; null for the window.
 */
function scrollingBoxOf(grid) {
    const view = grid.ownerDocument.defaultView;
    if (!view)
        return null;
    const scrolls = (node) => /^(auto|scroll)$/.test(view.getComputedStyle(node).overflowY);
    for (let node = grid; node; node = node.parentElement ?? node.getRootNode().host ?? null) {
        if (node instanceof view.HTMLElement && scrolls(node))
            return node;
    }
    return null;
}
const NOT_LAID_OUT = { columns: 0, fromRow: 0, toRow: 0 };
/** What the grid shows now: its columns from its width and the rows on screen from where its scrolling box sits over it. */
function measureGrid(grid, count, rated) {
    const rect = grid.getBoundingClientRect();
    if (!(rect.width > 0))
        return NOT_LAID_OUT;
    const columns = mascotGridColumns(rect.width, rated);
    const box = scrollingBoxOf(grid);
    let viewTop;
    let viewBottom;
    if (box === grid) {
        viewTop = grid.scrollTop;
        viewBottom = grid.scrollTop + grid.clientHeight;
    }
    else if (box) {
        const boxRect = box.getBoundingClientRect();
        viewTop = boxRect.top - rect.top;
        viewBottom = boxRect.bottom - rect.top;
    }
    else {
        viewTop = -rect.top;
        viewBottom = (grid.ownerDocument.defaultView?.innerHeight ?? 0) - rect.top;
    }
    const { fromRow, toRow } = mascotGridRows({ count, columns, rated, viewTop, viewBottom });
    return { columns, fromRow, toRow };
}
/** The rows to draw: the measured window and every pinned tile's row, as runs of consecutive rows in order. */
export function mascotGridSegments(measured, count, pinned) {
    const rows = measured.columns > 0 ? Math.ceil(count / measured.columns) : 0;
    const wanted = new Set();
    for (let row = measured.fromRow; row < measured.toRow; row += 1)
        wanted.add(row);
    for (const at of pinned)
        if (at >= 0 && at < count && measured.columns > 0)
            wanted.add(Math.floor(at / measured.columns));
    const segments = [];
    for (const row of [...wanted].filter((row) => row < rows).sort((a, b) => a - b)) {
        const last = segments[segments.length - 1];
        if (last && last[1] === row)
            last[1] = row + 1;
        else
            segments.push([row, row + 1]);
    }
    return segments;
}
/**
 * One gallery grid (the flat gallery, or one category's), a radio group of its tiles, drawing the rows on screen with
 * MASCOT_GRID_OVERSCAN_ROWS more each way and the pinned tiles' rows, spacers holding the rest. It measures itself on
 * mount and whenever it, its scrolling box or the window changes size (ResizeObserver, where there is one) or anything
 * scrolls (one frame at a time), and re-renders only when the rows to draw change. Until measured (its first render, a
 * server render) and without a layout it draws the first MASCOT_GRID_FALLBACK_TILES tiles and the pinned ones.
 */
export function MascotTileGrid({ gridKey, tiles, rated, pinned, cell, onRendered, gridRef, id, className, onKeyDown, ...aria }) {
    const ref = useRef(null);
    // Null until the first measure, which runs before the first paint: the first tiles are drawn (a server render has
    // nothing else), and nothing is reported until the measure says what is on screen.
    const [measured, setMeasured] = useState(null);
    const count = tiles.length;
    useLayoutEffect(() => {
        const grid = ref.current;
        if (!grid)
            return;
        const view = grid.ownerDocument.defaultView;
        let frame = null;
        const measure = () => {
            frame = null;
            const next = measureGrid(grid, count, rated);
            setMeasured((current) => (current && current.columns === next.columns && current.fromRow === next.fromRow && current.toRow === next.toRow ? current : next));
        };
        const soon = () => {
            if (frame !== null || !view)
                return;
            frame = view.requestAnimationFrame(measure);
        };
        measure();
        if (!view)
            return;
        // Every scroll anywhere (the page, the chat, the grid itself) reaches the document in its capture phase.
        grid.ownerDocument.addEventListener('scroll', soon, { capture: true, passive: true });
        view.addEventListener('resize', soon);
        const observer = typeof view.ResizeObserver === 'function' ? new view.ResizeObserver(soon) : null;
        if (observer) {
            observer.observe(grid);
            const box = scrollingBoxOf(grid);
            if (box && box !== grid)
                observer.observe(box);
        }
        return () => {
            if (frame !== null)
                view.cancelAnimationFrame(frame);
            grid.ownerDocument.removeEventListener('scroll', soon, { capture: true });
            view.removeEventListener('resize', soon);
            observer?.disconnect();
        };
    }, [count, rated]);
    const pinnedAt = pinned.map((tileId) => tiles.findIndex((tile) => tile.id === tileId)).filter((at) => at >= 0);
    const pitch = mascotGridRowPitch(rated);
    const parts = [];
    const drawn = [];
    if (measured === null || measured.columns === 0) {
        // Not measured yet (the first render, a server render), or no layout: the first tiles, and the pinned ones after
        // them when they are further down.
        const first = tiles.slice(0, MASCOT_GRID_FALLBACK_TILES);
        drawn.push(...first, ...pinnedAt.filter((at) => at >= MASCOT_GRID_FALLBACK_TILES).sort((a, b) => a - b).map((at) => tiles[at]));
        parts.push(...drawn.map(cell));
    }
    else {
        const rows = Math.ceil(count / measured.columns);
        let drawnTo = 0;
        const spacer = (fromRow, toRow) => {
            if (toRow <= fromRow)
                return;
            // Rows that are not drawn: their height, less the gap the grid puts after the spacer itself.
            parts.push(_jsx("div", { className: "gyro-mascot-rows", "aria-hidden": "true", style: { height: (toRow - fromRow) * pitch - MASCOT_GRID.gapPx } }, `rows-${fromRow}`));
        };
        for (const [fromRow, toRow] of mascotGridSegments(measured, count, pinnedAt)) {
            spacer(drawnTo, fromRow);
            const segment = tiles.slice(fromRow * measured.columns, Math.min(count, toRow * measured.columns));
            drawn.push(...segment);
            parts.push(...segment.map(cell));
            drawnTo = toRow;
        }
        spacer(drawnTo, rows);
    }
    // Reported once measured (before that the tiles drawn are a guess), whenever the ids drawn change; the array itself is
    // new on every render. A grid that goes is forgotten by the gallery (useDrawnTiles), never reported from here.
    const drawnKey = measured === null ? null : drawn.map((tile) => tile.id).join(' ');
    useEffect(() => {
        if (drawnKey !== null)
            onRendered(gridKey, drawnKey ? drawn : []);
    }, [gridKey, drawnKey, onRendered]); // eslint-disable-line react-hooks/exhaustive-deps -- drawn changes exactly when drawnKey does
    const setRefs = (element) => {
        ref.current = element;
        if (typeof gridRef === 'function')
            gridRef(element);
        else if (gridRef)
            gridRef.current = element;
    };
    return _jsx("div", { id: id, className: className, role: "radiogroup", "aria-label": aria['aria-label'], "aria-busy": aria['aria-busy'] || undefined, ref: setRefs, onKeyDown: onKeyDown, "data-columns": measured?.columns || undefined, children: parts });
}
//# sourceMappingURL=mascotTileGrid.js.map